import React, { useState, useRef, useCallback, useContext } from 'react';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_POSTE } from '../../../Reducers/Actions';
import { Alert, Box } from '@mui/material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';
import FormInput from '../../../SystemDesign/FormInput';
import { useCreatePosteWithRequirementsMutation } from '../../../services/SkillsManagement';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import AddIcon from '@mui/icons-material/Add';
import { AddRequirementForm } from '../../SkillRequirements/AddRequirementsSkills';

const AddPoste = ({ open, onClose, onSubmit }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Use SnackBar context for displaying messages
  const { setOpenSnackBar, setInfoAlert } = useContext(SnackBarContext);
  
  // State to track form validity and dirty state
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  // Track selected skills count from AddRequirementForm to enforce at least one
  const [selectedCount, setSelectedCount] = useState(0);
  
  // Create a ref to access Formik methods from outside the Formik component
  const formRef = useRef(null);
  const reqFormRef = useRef(null);
  
  // RTK Query mutation hook
  const [createPosteWithRequirements, { isLoading }] = useCreatePosteWithRequirementsMutation();
  
  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Poste name is required')
  });
  
  // Function to trigger form submission via MuiDialog action button
  const submitForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      // Start submission
      setSubmitting(true);
      
      // Collect selected requirement items from the right panel
      const selectedRequirements =
        (reqFormRef && reqFormRef.current && typeof reqFormRef.current.getItems === 'function'
          ? reqFormRef.current.getItems()
          : []) || [];

      // Enforce at least one required skill selected
      if (!selectedRequirements || selectedRequirements.length === 0) {
        setInfoAlert({
          severity: 'warning',
          message: 'Please select at least one required skill.'
        });
        setOpenSnackBar(true);
        return;
      }

      // Call the API to create the poste
      // Using combined endpoint: create poste + (optional) requirements
      // Keep current UI, so send empty requirements array with correct structure
      const result = await createPosteWithRequirements({
        body: {
          name: values.name.trim(),
          // discipline_id can be added here when UI supports it
          requirements: selectedRequirements // [{ skill_id, min_score?, max_score?, required_level? }]
        }
      }).unwrap();
      
      // Extract the ID from the response, checking nested 'poste' first
      let posteData = result;
      let posteId = '';

      const posteObjectPeek = (result && (result.poste || result)) || {};
      if (posteObjectPeek) {
        // Prefer standard 'id' or 'posteId' found on the poste object
        posteId = posteObjectPeek.posteId || posteObjectPeek.id || '';
      }
      
      if (!posteId && result) {
        // Fallbacks for different possible response formats
        if (result.data && (result.data.id || result.data.posteId)) {
          // Format: { data: { id/posteId: '...' } }
          posteData = result.data;
          posteId = result.data.posteId || result.data.id;
        } else if (result.id) {
          // Format: { id: '...' }
          posteId = result.id;
        } else if (result.posteId) {
          // Format: { posteId: '...' }
          posteId = result.posteId;
        } else if (result._id) {
          // MongoDB style id
          posteId = result._id;
        } else {
          // If there's only one property and it looks like an ID
          const keys = Object.keys(result);
          if (keys.length === 1 && (keys[0].includes('id') || keys[0].includes('Id'))) {
            posteId = result[keys[0]];
          }
        }
      }
      
      if (!posteId) {
        // We still couldn't find an ID, but the operation was successful
        // Use a timestamp as fallback ID if no ID was found in response
        const fallbackId = `temp_${Date.now()}`;
        posteId = fallbackId;
      }
      
      // Extract poste data correctly from the response
      // The API returns the data in a nested 'poste' property
      const posteObject = result.poste || result;
      
      // Create a proper poste object with all required fields
      const posteForStore = {
        posteId: posteObject.posteId || posteObject.id || posteId, // prefer nested poste object ids
        name: values.name,
        createdAt: posteObject.createdAt || new Date().toISOString(),
        updatedAt: posteObject.updatedAt || new Date().toISOString(),
        createdBy: posteObject.createdBy || null, // Include the createdBy information
        users: posteObject.users || [],
      };
      
      // Dispatch to Redux store
      dispatch({ type: ADD_POSTE, payload: posteForStore });
      
      // Notify parent component
      if (onSubmit) {
        onSubmit(posteForStore);
      }
      
      // Show success message in SnackBar
      setInfoAlert({
        severity: 'success',
        message: `Poste "${values.name}" successfully created`
      });
      setOpenSnackBar(true);
      
      // Reset form and close dialog
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating poste:', error);
      // Show error message in SnackBar instead of inline form error
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || 'Failed to create poste'
      });
      setOpenSnackBar(true);
    } finally {
      // Always make sure to finish submission state
      setSubmitting(false);
    }
  };

  // State to preserve form values between reinitializations
  const [formValues, setFormValues] = useState({
    name: ''
  });

  // Create dialog content for MuiDialog
  const dialogContent = () => (
    <Formik
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      innerRef={formRef}
      validateOnMount={true}
      enableReinitialize={false}
    >
      {(formikProps) => {
        // Update parent about form state changes without using hooks
        const { isValid, dirty } = formikProps;
        if (formRef.current && (formRef.current.isValid !== isValid || formRef.current.dirty !== dirty)) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }
        
        return (
          <Form id="add-poste-form">  
            {/* Form fields */}
            <Box sx={{ mb: 3 }}>
              <FormInput
                name="name"
                label="Poste Name"
                placeholder="Enter the poste name..."
                variant="outlined"
              />
            </Box>

            {/* Requirement skills selector (inline) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ fontSize: 12, fontWeight: 600, color: colors.grey[600] }}>Required Skills *</Box>
              <Box sx={{ height: 380, border: `1px solid ${colors.grey[300]}`, borderRadius: 1, p: 1, overflow: 'hidden' }}>
                {selectedCount === 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    At least one skill is required to create this poste. Please select a skill below.
                  </Alert>
                )}
                <AddRequirementForm
                  ref={reqFormRef}
                  poste={null}
                  existingRequirements={[]}
                  showFooter={false}
                  onSelectedChange={(count) => setSelectedCount(count)}
                />
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <MuiDialog 
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      icon={<AddIcon />}
      title="Create New Poste"
      subtitle="Add a new poste to the system"
      content={dialogContent}
      existAction={true}
      actionTitle="Create Poste"
      actionFunction={submitForm}
      isLoading={isLoading}
      actionType="submit"
      formId="add-poste-form"
      disableAction={!(isFormValid && isFormDirty && selectedCount > 0)}
      
    />
  );
};

export default AddPoste;
